import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { hashToUnit } from "../lib/format";
import { EASE_OUT } from "../lib/motion";

function Row({ tag, hashes, otherSet, delay }) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="fp__row">
      <span className="fp__tag">{tag}</span>
      <div className="fp__cells">
        {hashes.map((hash, index) => {
          const matched = otherSet.has(String(hash));
          const height = 0.28 + hashToUnit(hash) * 0.72;
          return (
            <motion.span
              className="fp__cell"
              key={`${hash}-${index}`}
              data-match={matched}
              style={{ height: `${height * 100}%` }}
              initial={{ scaleY: prefersReducedMotion ? 1 : 0, opacity: prefersReducedMotion ? 1 : 0 }}
              whileInView={{ scaleY: 1, opacity: 1 }}
              viewport={{ once: true, margin: "0px 0px -8% 0px" }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : {
                      duration: 0.34,
                      ease: EASE_OUT,
                      // Small per-cell stagger: 64 cells still resolve in ~0.8s.
                      delay: delay + index * 0.008,
                    }
              }
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * The winnowed fingerprint sets for both documents, side by side.
 *
 * Bar height is a stable fold of each hash, so the strip is a visual signature
 * rather than meaningful magnitude; the information carried is *which* bars are
 * matched, and those are both filled and accented. The overlap count below
 * states the same fact in text.
 */
function FingerprintStrip({ one = [], two = [], shared = 0, totalOne = 0, totalTwo = 0 }) {
  const setOne = useMemo(() => new Set(one.map(String)), [one]);
  const setTwo = useMemo(() => new Set(two.map(String)), [two]);

  if (!one.length && !two.length) return null;

  return (
    <div className="fp">
      <Row tag="Doc 1" hashes={one} otherSet={setTwo} delay={0} />
      <Row tag="Doc 2" hashes={two} otherSet={setOne} delay={0.12} />
      <p className="fp__overlap">
        <strong className="accent">{shared}</strong>
        <span>
          fingerprints shared across {totalOne} / {totalTwo} winnowed hashes
          {one.length < totalOne && " · showing first 64"}
        </span>
      </p>
    </div>
  );
}

export default FingerprintStrip;
